import PropTypes from "prop-types";
import React from "react";
import ValidationTreeStore from "data/ValidationTreeStore";
import Dispatcher from "Dispatcher";
import ValidationTreeActions from "data/ValidationTreeActions";
import ValidationTree from "../components/ValidationTree";
import treeItemPredicate from "../treeItemPredicate";
import TableStore from "data/TableStore";
import entityMetaStore from "data/meta/entityMetaStore";
import entityFieldMetaStore from "data/meta/entityFieldMetaStore";
import _or_validate, { buildState } from "jsmodel/_or_validate";
import {
    getKeyPrefix,
    isIllegal,
    isOk,
    isWarning,
    isBlocking,
} from "jsmodel/utils";
import {
    isAbstractAssoc,
    isInverseAssoc,
} from "data/util/EntityProxy";
import {
    getTopLevelRecords,
    rerouteFieldNav,
} from "data/selectors/superRecords";
import SuperRecordStore from "data/SuperRecordStore";
import StereotypesStore from "data/StereotypesStore";
import { isCreatableFrom } from "data/selectors/stereotypes";
import EntityName from "data/util/EntityName";
import NavigatorStore from "data/NavigatorStore";
import ValidationExcludeStore from "data/ValidationExcludeStore";
import error from "jsmodel/error";
import useFluxStore from "@armus/use-flux-store";
import safeContextDispatch from "data/util/safeContextDispatch";
import NavigationActions from "data/NavigationActions";

// impure!
const updateContainment = (me, kid) => {
    if (! me._contains_blocking) {
        me._contains_blocking = kid._is_blocking || kid._contains_blocking;
    }
    if (! me._contains_illegal) {
        me._contains_illegal = kid._is_illegal || kid._contains_illegal;
    }
    if (! me._contains_warning) {
        me._contains_warning = kid._is_warning || kid._contains_warning;
    }
    if (! me._contains_ok) {
        me._contains_ok = kid._is_ok || kid._contains_ok;
    }
};

export const buildExcludesFilter = implKey => it => {
    if (it.fieldKey == null || it.entityName == null) {
        return true;
    }
    return !ValidationExcludeStore.isIncluded(
        implKey,
        it.entityName,
        it.fieldKey);
};

const metaSkeleton = entityKey => ({
    entityName: new EntityName(entityKey).bareEntityKey, // this is questionable at best...
    _is_blocking: false,
    _is_illegal: false,
    _is_warning: false,
    _is_ok: false,
    _contains_blocking: false,
    _contains_illegal: false,
    _contains_warning: false,
    _contains_ok: false,
});

const assembleItem = (meta, entityKey, recordId, cid, message, children, onNavToField) => ({
    meta,
    data: {
        entityKey,
        recordId,
        cid,
        message,
        state: meta._is_blocking
            ? "blocking"
            : meta._is_illegal
                ? "illegal"
                : meta._is_warning
                    ? "warning"
                    : meta._is_ok
                        ? "ok"
                        : "unknown",
        nestedState: meta._contains_blocking
            ? "blocking"
            : meta._contains_illegal
                ? "illegal"
                : meta._contains_warning
                    ? "warning"
                    : meta._contains_ok
                        ? "ok"
                        : "unknown",
        children,
        fieldKey: meta.fieldKey,
        onNavToField,
    },
});

const buildFieldItem = (ep, fi, excludeFilter, checkboxFilter, onNavToField) => {
    const entityKey = ep.entityKey;
    const value = ep.get(fi);
    const meta = metaSkeleton(entityKey);
    meta.fieldKey = fi.key;
    const state = excludeFilter(meta)
        ? _or_validate(fi, value, ep)
        : buildState(fi, error.OK.code);
    meta._is_blocking = isBlocking(state.code);
    meta._is_illegal = isIllegal(state.code);
    meta._is_warning = isWarning(state.code);
    meta._is_ok = isOk(state.code);
    updateContainment(meta, meta);
    const children = value != null && isAbstractAssoc(fi) && !isInverseAssoc(fi)
        ? (Array.isArray(value)
            ? value
            : [value])
            .map(c =>
                buildRecordItem(c, excludeFilter, checkboxFilter, onNavToField))
            .map(it => { // update containment (passthrough)
                updateContainment(meta, it.meta);
                return it;
            })
            .filter(it => checkboxFilter(it.meta))
            .map(it => it.data)
        : undefined;
    return assembleItem(
        meta,
        entityKey,
        ep.id,
        fi.key,
        state.message,
        children,
        onNavToField,
    );
};

export const buildRecordItem = (ep, excludeFilter, checkboxFilter, onNavToField) => {
    const entityKey = ep.entityKey;
    const meta = metaSkeleton(entityKey);
    const children = entityFieldMetaStore.getAllInfos(
        entityKey,
        it => {
            const isNotMrP = !it.mrPointer;
            const isDqr = it.dqr;
            const isAbstractAssocOrDqr = isDqr || isAbstractAssoc(it);
            const isNotInverseAssocOrDqr = isDqr || !isInverseAssoc(it);
            return isNotMrP && isAbstractAssocOrDqr && isNotInverseAssocOrDqr;
        },
    )
        .map(fi => buildFieldItem(ep, fi, excludeFilter, checkboxFilter, onNavToField))
        .filter(it => {
            if (it.data.children && it.data.children.length) return true;
            return excludeFilter(it.meta);
        })
        .map(it => { // update containment (passthrough)
            updateContainment(meta, it.meta);
            return it;
        })
        .filter(it => checkboxFilter(it.meta))
        .map(it => it.data);
    meta._is_blocking = meta._contains_blocking;
    meta._is_illegal = meta._contains_illegal;
    meta._is_warning = meta._contains_warning;
    meta._is_ok = meta._contains_ok;
    return assembleItem(
        meta,
        entityKey,
        ep.id,
        ep.getKey(),
        ep.getLabel(),
        children,
        onNavToField,
    );
};

const addMagicChildren = (item, ep, mcis, mrInfo, excludeFilter, checkboxFilter, onNavToField) => {
    if (mcis.length > 0) {
        const meta = metaSkeleton(ep.entityKey);
        meta._is_ok = true;
        const coll = assembleItem(
            meta,
            ep.entityKey,
            ep.id,
            mrInfo.magicChildKey,
            StereotypesStore.getStereotypeByKey(mrInfo.magicChildKey).pluralName,
            mcis
                .map(it => { // update containment (passthrough)
                    updateContainment(meta, it.meta);
                    return it;
                })
                .filter(it => checkboxFilter(it.meta))
                .map(it => it.data),
            onNavToField,
        );
        updateContainment(item.meta, coll.meta);
        if (!item.data.children) {
            item.data.children = [];
        }
        item.data.children.push(coll.data);
    }
};

const buildRootItem = (ep, excludeFilter, checkboxFilter, allLevels, ctx, onNavToField) => {
    const mrInfo = StereotypesStore.getMagicRoot(ep.entityKey);
    if (isCreatableFrom(mrInfo, ep.entityKey)) {
// 1.  A magic root record is open for editing. In this case the root of the tree
//     is the active record, with the magic edge represented in the tree as a
//     stereotypical association field, including the magic children which are the
//     same version as the root. The "All Levels" checkbox does nothing.
        // it's a magic root record
        const item = buildRecordItem(ep, excludeFilter, checkboxFilter, onNavToField);
        const versionPrefix = getKeyPrefix(ep.entityKey) + "/";
        const srLO = SuperRecordStore.getRecordLO(
            mrInfo.key,
            ep.id,
        );
        if (srLO.hasValue()) {
            // if the superrecord isn't loaded yet, can't do the kids.
            const mcis = getTopLevelRecords(TableStore, srLO.getValueEnforcing())
                .filter(r => r.entityKey.indexOf(versionPrefix) === 0)
                .map(r => buildRecordItem(r, excludeFilter, checkboxFilter, onNavToField));
            addMagicChildren(item, ep, mcis, mrInfo, excludeFilter, checkboxFilter, onNavToField);
        }
        return item;
    } else if (allLevels) {
// 1.  A non-magic root record is open for editing, w/ "All Levels" _checked_. In
//     this case the root of the tree is the active record's top-level record's
//     magic root record with the magic edge represented in the tree as a
//     stereotypical association field, including only the top-level record
//     described above (not any of it's peers at the version, implementation, or
//     magic levels).
        // find ep's TLR
        let ppk;
        let ei;
        while ((ppk = (ei = entityMetaStore.getInfo(ep.entityKey)).parentPointerKey)) {
            ep = ep.get(ppk);
        }
        // build an item for that guy
        const item = buildRecordItem(ep, excludeFilter, checkboxFilter, onNavToField);
        if (ei.magicRootPointerKey) {
            // flip the magic root over
            const mrep = ep.get(ei.magicRootPointerKey);
            const mri = buildRecordItem(mrep, excludeFilter, checkboxFilter, onNavToField);
            addMagicChildren(mri, mrep, [item], mrInfo, excludeFilter, checkboxFilter, onNavToField);
            return mri;
        }
        return item;
    } else {
// 1.  A non-magic root record is open for editing, w/ "All Levels" _unchecked_.
//     This is the simple case; the root of the tree is the active record.
        return buildRecordItem(ep, excludeFilter, checkboxFilter, onNavToField);
    }
};

const ValidationTreeContainer = (props) => {
    const { fluxContext } = props;
    const state = useFluxStore(() => {
        const filters = ValidationTreeStore.getFilters(fluxContext);
        const checkboxFilter = treeItemPredicate(filters);
        const recState = NavigatorStore.getRecordState(fluxContext);
        let item = null;
        if (recState != null) {
            const activeRecord = TableStore.getEntityProxy(
                recState.key,
                recState.id,
            );
            const implKey = new EntityName(activeRecord.entityKey).implKey;
            item = buildRootItem(
                TableStore.getEntityProxy(activeRecord.entityKey, activeRecord.id),
                buildExcludesFilter(implKey),
                checkboxFilter,
                filters.all,
                fluxContext,
                (entityKey, recordId, fieldKey) => {
                    const magic = NavigatorStore.getMagicState(fluxContext);
                    [entityKey, recordId, fieldKey] =
                        rerouteFieldNav(magic, entityKey, recordId, fieldKey);
                    safeContextDispatch(fluxContext, {
                        type: NavigationActions.SELECT_RECORD,
                        entityKey,
                        recordId,
                        fieldKey,
                    });
                },
            );
        }

        return {
            filters,
            toggleFilter: filter => {
                Dispatcher.contextDispatch(fluxContext, {
                    type: ValidationTreeActions.TOGGLE_FILTER,
                    filter,
                });
            },
            // the real ones
            item: item && checkboxFilter(item.meta) ? item.data : null,
            ctx: fluxContext + ":validation",
        };
    }, [
        ValidationTreeStore,
        TableStore,
        SuperRecordStore,
        NavigatorStore,
        ValidationExcludeStore,
    ], [fluxContext]);
    return <ValidationTree {...state} />;
};

ValidationTreeContainer.propTypes = {
    fluxContext: PropTypes.string.isRequired,
};

export default ValidationTreeContainer;



// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/validationtree/containers/ValidationTreeContainer.js